import React from 'react';
import { 
  SkeletonHero, 
  SkeletonPricing,
  SkeletonTitle,
  SkeletonText,
  SkeletonParagraph,
  SkeletonButton,
  SkeletonCard,
  Skeleton
} from '../ui/skeletons';

// Course Page Full Skeleton (works for both group and individual)
const CoursePageSkeleton = ({ accentColor = "#9BAF8F" }) => (
  <div className="bg-[#FAF6F1] min-h-screen overflow-x-hidden">
    {/* Header Skeleton */}
    <div className="fixed top-0 left-0 right-0 z-50 px-3 py-3">
      <div className="bg-white/80 backdrop-blur-md rounded-2xl px-6 py-4 flex items-center justify-between">
        <Skeleton className="w-32 h-8 rounded-lg" />
        <div className="hidden lg:flex items-center gap-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="w-20 h-4 rounded" />
          ))}
        </div>
        <SkeletonButton size="md" />
      </div>
    </div>

    <main>
      {/* Hero Section Skeleton */}
      <section className="relative min-h-[60vh] overflow-hidden p-3 pt-20">
        <div className="relative rounded-3xl overflow-hidden h-[calc(60vh-1.5rem)]">
          <Skeleton className="absolute inset-0 rounded-3xl" />
          <div className="absolute bottom-12 left-8 lg:left-16 space-y-4 z-10">
            <SkeletonText width="100px" className="h-3" />
            <SkeletonTitle size="2xl" className="w-[300px] lg:w-[450px]" />
            <SkeletonParagraph lines={2} className="w-[250px] lg:w-[400px]" />
          </div>
        </div>
      </section>

      {/* Quick Info Bar Skeleton */}
      <section className="py-4 px-3">
        <div className="window-content bg-white rounded-3xl py-6 px-8">
          <div className="max-w-[1200px] mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="w-12 h-12 rounded-xl" />
                  <div className="space-y-1">
                    <SkeletonText width="60px" className="h-3" />
                    <SkeletonText width="90px" className="h-5" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section Skeleton */}
      <section className="py-4 px-3">
        <div className="window-content bg-gradient-to-br from-[#F5E6D3] via-[#FAF0E6] to-[#F5E6D3] rounded-3xl py-12 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Left Content */}
              <div className="flex-1 space-y-8">
                {/* What's Included */}
                <div className="bg-white rounded-3xl p-6 lg:p-8 space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Skeleton className="w-10 h-10 rounded-xl" />
                    <div className="space-y-1">
                      <SkeletonTitle size="md" className="w-40" />
                      <SkeletonText width="100px" className="h-3" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="flex items-start gap-3 p-4 bg-[#FAF6F1] rounded-xl">
                        <Skeleton className="w-8 h-8 rounded-lg flex-shrink-0" />
                        <div className="flex-1 space-y-1">
                          <SkeletonText width="70%" className="h-4" />
                          <SkeletonText width="90%" className="h-3" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Course Modules */}
                <div className="bg-white rounded-3xl p-6 lg:p-8 space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Skeleton className="w-10 h-10 rounded-xl" />
                    <div className="space-y-1">
                      <SkeletonTitle size="md" className="w-48" />
                      <SkeletonText width="120px" className="h-3" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="bg-[#FAF6F1] rounded-2xl p-5 space-y-3">
                        <div className="flex items-center gap-2">
                          <Skeleton className="w-8 h-8 rounded-full" />
                          <SkeletonText width="100px" className="h-4" />
                        </div>
                        <SkeletonTitle size="sm" className="w-32" />
                        <SkeletonParagraph lines={2} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Testimonials */}
                <div className="bg-white rounded-3xl p-6 lg:p-8 space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Skeleton className="w-10 h-10 rounded-xl" />
                    <div className="space-y-1">
                      <SkeletonTitle size="md" className="w-44" />
                      <SkeletonText width="100px" className="h-3" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {Array.from({ length: 2 }).map((_, i) => (
                      <div key={i} className="bg-[#FAF6F1] rounded-2xl p-5 space-y-4">
                        <div className="flex gap-1">
                          {Array.from({ length: 5 }).map((_, j) => (
                            <Skeleton key={j} className="w-3 h-3 rounded" />
                          ))}
                        </div>
                        <SkeletonParagraph lines={3} />
                        <div className="flex items-center gap-2">
                          <Skeleton className="w-8 h-8 rounded-full" />
                          <div className="space-y-1">
                            <SkeletonText width="80px" className="h-3" />
                            <SkeletonText width="60px" className="h-2" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="bg-[#FAF6F1] rounded-3xl p-6">
                  <SkeletonTitle size="sm" className="w-36 mb-4" />
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="bg-white rounded-xl p-4 text-center space-y-2">
                        <Skeleton className="w-8 h-8 mx-auto rounded" />
                        <SkeletonText width="60%" className="mx-auto h-3" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="w-full lg:w-[340px] shrink-0">
                <SkeletonPricing />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section Skeleton */}
      <section className="py-4 px-3 pb-6">
        <div className="window-content bg-gradient-to-br from-[#FFDAB3] to-[#FFD0A0] rounded-3xl py-12 px-8">
          <div className="max-w-[800px] mx-auto text-center">
            <SkeletonTitle size="xl" className="mx-auto w-80 mb-4" />
            <Skeleton className="h-6 w-96 mx-auto rounded mb-8" />
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <SkeletonButton size="xl" className="w-48" />
              <SkeletonButton size="xl" className="w-44" />
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
);

export { CoursePageSkeleton };
